import { SerializedQuestionImageCache } from '../question-image/question-image.model';
import { QuestionItemV2 } from '../question/question.model';

export type MarkschemeDisplayElement = 'all_mark_groups' | 'solution_paths';

export enum ImageEditingType {
  Original = 'original',
  Generated = 'generated',
}

export interface QuestionEditingOptions {
  /**
   * Array of image editing types that are allowed.
   * - 'original': allows uploading/deleting original images
   * - 'generated': allows access to Code and Manual Upload tabs
   */
  allowImageEditing: ImageEditingType[];
  /**
   * When set to `true`, the question parts (stem, stimulus, stem group) can be edited.
   * Not applies for the images or markscheme subparts.
   */
  allowQuestionEditing: boolean;
  /**
   * When set to `true`, the markscheme of stem and stem group's stems can be edited.
   */
  allowMarkschemeEditing: boolean;
  /**
   * When set to `true`, allows editing of multiple choice question options.
   */
  allowMcqEditing: boolean;
  /**
   * When set to `true`, allows structural changes to markschemes like deleting mark groups,
   * adding new mark groups, adding notes, and regenerating solution paths.
   */
  allowMarkschemeStructureEditing?: boolean;
  /**
   * When set to `true`, allows structural changes to MCQ options like adding/removing options,
   * reordering options, and deleting the entire Options item.
   */
  allowMcqStructureEditing?: boolean;
  /**
   * Controls which markscheme elements to display in the markscheme editor/viewer.
   * Defaults to showing both all mark groups and solution paths.
   */
  displayMarkschemeElements?: MarkschemeDisplayElement[];
  /**
   * When set to `true`, hides markscheme metadata fields like ID, marks, topic ID, etc.
   */
  hideMarkschemeMetadata?: boolean;
  /**
   * When set to `true`, hides all STEM, MG, and other selector badges instead of disabling them.
   */
  hideBadges: boolean;

  /**
   * When set to `true`, shows MCQ metadata fields like layout, marks, etc.
   */
  showMcqMetadata?: boolean;
  /**
   * When set to `true`, allows structural changes to question parts like adding/removing items,
   * reordering items, and deleting the entire item.
   */
  allowQuestionStructureEditing?: boolean;

  /**
   * Hides the markscheme editor/viewer.
   */
  hideMarkscheme?: boolean;

  /**
   * Hides the image and table metadata fields like ID, marks, topic ID, etc.
   */
  hideImageAndTableMetadata?: boolean;

  /**
   * When set to `true`, hides the question details header ( max marks, difficulty, etc.).
   */
  hideQuestionDetailsHeader?: boolean;
}

export interface ContentItemEditingOptions {
  /**
   * When set to `true`, the text can be edited.
   */
  allowTextEditing: boolean;
  /**
   * Array of image editing types that are allowed.
   * - 'original': allows uploading/deleting original images
   * - 'generated': allows access to Code and Manual Upload tabs
   */
  allowImageEditing: ImageEditingType[];

  /**
   * When set to `true`, the fill in the blanks can be edited.
   */
  allowFillInTheBlanks: boolean;

  /**
   * Hides the image and table metadata fields like ID, marks, topic ID, etc.
   */
  hideImageAndTableMetadata?: boolean;
}

export interface QuestionEditorState {
  questionItems: QuestionItemV2[];
  imageCache: SerializedQuestionImageCache;
  timestamp: number;
}
